import React from 'react';

/**
 * Button Component
 * Award-Winning Design System
 * 
 * Variants: primary, secondary, ghost, icon
 * Sizes: sm (36px), md (40px), lg (48px)
 */

const Button = ({ 
  children, 
  variant = 'primary',
  size = 'md',
  onClick, 
  disabled = false,
  type = 'button',
  className = '',
  icon = null,
  iconPosition = 'left',
  fullWidth = false,
  loading = false,
  ...props
}) => {
  // Base button class
  let buttonClass = 'btn';
  
  // Variant classes
  switch (variant) {
    case 'primary':
      buttonClass += ' btn-primary';
      break;
    case 'secondary':
      buttonClass += ' btn-secondary';
      break;
    case 'ghost':
      buttonClass += ' btn-ghost';
      break;
    case 'icon':
      buttonClass += ' btn-icon';
      break;
    default:
      buttonClass += ' btn-primary';
  }
  
  // Size classes
  switch (size) {
    case 'sm':
      buttonClass += ' btn-sm';
      break;
    case 'lg':
      buttonClass += ' btn-lg';
      break;
    default:
      // md is default, no extra class needed
      break;
  }
  
  // Full width
  if (fullWidth) {
    buttonClass += ' w-full';
  }
  
  // Add custom classes
  if (className) {
    buttonClass += ` ${className}`;
  }

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={buttonClass}
      {...props}
    >
      {loading && (
        <span className="loading-spinner loading-spinner-sm" style={{ marginRight: children ? '8px' : 0 }} />
      )}
      {!loading && icon && iconPosition === 'left' && (
        <span style={{ display: 'flex', marginRight: children ? '8px' : 0 }}>
          {icon}
        </span>
      )}
      {children && <span>{children}</span>}
      {!loading && icon && iconPosition === 'right' && (
        <span style={{ display: 'flex', marginLeft: children ? '8px' : 0 }}>
          {icon}
        </span>
      )}
    </button>
  );
};

/**
 * Button Group Component
 * Groups buttons together with proper spacing
 */
export const ButtonGroup = ({ children, align = 'left', gap = 'md', className = '' }) => {
  const gapClasses = {
    sm: 'gap-2',
    md: 'gap-3',
    lg: 'gap-4'
  };

  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between'
  };

  return (
    <div className={`flex items-center ${alignClasses[align]} ${gapClasses[gap]} ${className}`}>
      {children}
    </div>
  );
};

/**
 * Icon Button Component
 * Specialized button for icon-only buttons
 */
export const IconButton = ({ 
  icon,
  onClick,
  disabled = false,
  size = 'md',
  variant = 'ghost',
  className = '',
  ariaLabel,
  ...props
}) => {
  return (
    <Button
      variant="icon"
      size={size}
      onClick={onClick}
      disabled={disabled}
      className={`btn-${variant} ${className}`}
      aria-label={ariaLabel}
      {...props}
    >
      {icon}
    </Button>
  );
};

export default Button;